import { useMemo, useRef, useState } from "react";
import * as R from "ramda";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { extent, max } from "d3-array";
import { forceSimulation, forceCollide, forceY } from "d3-force";
import { line } from "d3-shape";
import { scaleTime, scaleLinear } from "d3-scale";

import parseISO from "date-fns/fp/parseISO";

import Popup from "../../../../Common/js/components/Popup/Popup.jsx";

import TrendLine from "../../../../Common/js/components/Chart/TrendLine.jsx";
import YearHoverLine from "../../../../Common/js/components/Chart/YearHoverLine.jsx";
import XAxisTrend from "../../../../Common/js/components/Chart/XAxisTrend.jsx";
import YAxis from "../../../../Common/js/components/Chart/YAxis.jsx";
import YAxisRight from "../../../../Common/js/components/Chart/YAxisRight.jsx";
import { lineDataShape } from "../../../../Common/js/common/datacenterData.js";

const propTypes = {
  onYearHover: func.isRequired,
  hoveredYear: string,
  width: number.isRequired,
  height: number,
  dottedLineForMissingData: bool,
  excludeUrl: string,
  showLineLabels: bool,
  showValueOnHover: bool,
  events: arrayOf(
    shape({
      start: string.isRequired, // year
      end: string, // could be null
      text: string.isRequired,
      href: string, // could be null
      label: string, // could be null
    }),
  ),
  primaryData: lineDataShape.isRequired,
  secondaryData: lineDataShape,
  otherLinesData: arrayOf(lineDataShape),
};

const defaultProps = {
  events: [],
  height: 410,
  otherLinesData: [],
};

const NarrativeChart = (props) => {
  const {
    primaryData,
    secondaryData,
    otherLinesData,
    dottedLineForMissingData,
    excludeUrl,
    hoveredYear,
    onYearHover,
    showLineLabels,
    showValueOnHover,
  } = props;

  // filter out bad events (start is required)
  const events = R.reject(R.propSatisfies(R.isNil, "start"), props.events);

  const eventsAreaHeight = events && events.length > 0 ? 250 : 0;

  const svgWidth = props.width;
  const svgHeight = props.height;

  const [popupInfo, setPopupInfo] = useState();

  const svgRef = useRef();

  // space for axis, etc
  const margin = {
    top: 80,
    right: showLineLabels ? 120 : 60,
    bottom: 40,
    left: 60,
  };

  const width = svgWidth - margin.left - margin.right;
  const height = svgHeight - margin.top - margin.bottom;

  const hoveredDataPoint = R.find(
    R.propEq("timeFrame", hoveredYear),
    primaryData.dataPoints,
  );

  const allDataPoints = R.compose(
    R.unnest,
    R.pluck("dataPoints"),
  )([primaryData, secondaryData, ...otherLinesData]);

  // datatype or false
  const secondaryDataType = secondaryData && secondaryData.dataType;

  // datatype or false
  const otherLinesDataType =
    otherLinesData && otherLinesData.length > 0 && otherLinesData[0].dataType;

  /* X */

  const xScale = useMemo(() => {
    // eg, ["1999", "2020"]
    const eventYears = R.compose(R.pluck("start"), R.defaultTo([]))(events);

    // eg [Date, Date]
    const xDomain = R.compose(
      R.map(parseISO),
      extent,
      R.concat(eventYears), // include events
      R.pluck("timeFrame"),
    )(allDataPoints);

    return scaleTime().range([0, width]).domain(xDomain);
  }, [allDataPoints, events, width]);

  /* y1 */

  const y1Scale = useMemo(() => {
    const allY1Points = R.compose(
      R.unnest,
      R.pluck("dataPoints"),
      // add otherlines if the datatype is the same
      R.when(
        () => otherLinesDataType === primaryData.dataType,
        R.concat(otherLinesData),
      ),
      // add secondary if the datatype is the same
      R.when(
        () => secondaryDataType === primaryData.dataType,
        R.append(secondaryData),
      ),
    )([primaryData]);

    // always start the Y domain at 0
    const y1Domain = [0, R.compose(max, R.pluck("value"))(allY1Points)];

    return scaleLinear().range([height, 0]).domain(y1Domain).nice();
  }, [
    height,
    otherLinesData,
    otherLinesDataType,
    primaryData,
    secondaryData,
    secondaryDataType,
  ]);

  /* y2Scale, either for secondary or otherLines */

  const y2Scale = useMemo(() => {
    const allY2Points = R.compose(
      R.unnest,
      R.pluck("dataPoints"),
      R.when(
        () => otherLinesDataType && otherLinesDataType !== primaryData.dataType,
        R.concat(otherLinesData),
      ),
      R.when(
        () => secondaryDataType && secondaryDataType !== primaryData.dataType,
        R.append(secondaryData),
      ),
    )([]);

    // we don't need the scale if we don't have any data for this axis
    if (R.isEmpty(allY2Points)) {
      return;
    }

    // always start the Y domain at 0
    const y2Domain = [0, R.compose(max, R.pluck("value"))(allY2Points)];

    const y2Scale = scaleLinear().range([height, 0]).domain(y2Domain).nice();

    return y2Scale;
  }, [
    height,
    otherLinesData,
    otherLinesDataType,
    primaryData.dataType,
    secondaryData,
    secondaryDataType,
  ]);

  /* other lines */

  const getOtherLinesLabelPositionLookup = () => {
    if (!showLineLabels) {
      return;
    }

    // all lines (including the primary/secondary) to calc the label positions
    // (fx, fy, targetY) properties needed for force layout
    const allLabelPositions = R.compose(
      R.map((lineData) =>
        R.compose(
          R.assoc("fx", 0), // all should have a fixed x-position
          R.assoc(
            "targetY",
            y1Scale(R.compose(R.prop("value"), R.last)(lineData.dataPoints)),
          ),
        )(lineData),
      ),
      // make the primary line fixed in the y position
      R.append({
        ...primaryData,
        fx: y1Scale(
          R.compose(R.prop("value"), R.last, R.prop("dataPoints"))(primaryData),
        ),
      }),
      R.when(() => secondaryData, R.append(secondaryData)),
      R.concat(otherLinesData),
    )([]);

    // forceSimulation will add "y" property
    // https://www.placeiq.com/2017/08/pointsofinterestd3forcelayout/
    // https://github.com/d3/d3-force
    forceSimulation()
      .nodes(allLabelPositions)
      .force(
        "collide",
        // the primaryData label is bigger text, so the collide size is different
        forceCollide((d) => {
          return d.id === primaryData.id ? 26 / 2 : 16 / 2;
        }),
      )
      .force("y", forceY((d) => d.targetY).strength(1))
      .stop()
      .tick(3000);

    const positionLookup = R.indexBy(R.prop("id"), allLabelPositions);

    return positionLookup;
  };

  const renderOtherLines = () => {
    if (!otherLinesData || otherLinesData.length === 0) {
      return;
    }

    const otherLinesScale =
      otherLinesDataType === primaryData.dataType ? y1Scale : y2Scale;

    const lineGenerator = line()
      .x((d) => xScale(parseISO(d.timeFrame)))
      .y((d) => otherLinesScale(d.value))
      .defined((d) => d.value !== null);

    const positionLookup = getOtherLinesLabelPositionLookup();

    return (
      <g className="narrative-chart__other-locations">
        {otherLinesData
          .filter((lineData) => lineData.dataPoints.length > 0)
          .map((lineData) => {
            return (
              <g key={lineData.id}>
                <path
                  fill="none"
                  d={lineGenerator(lineData.dataPoints)}
                  stroke="currentColor"
                />
                {positionLookup && (
                  <text
                    fill={"currentColor"}
                    fontSize="14px"
                    strokeWidth="0"
                    x={xScale(xScale.domain()[1]) + 6}
                    y={positionLookup[lineData.id].y}
                    dy={"0.4em"}
                  >
                    {lineData.name}
                  </text>
                )}
              </g>
            );
          })}
      </g>
    );
  };

  /* events */

  // render the event dots in the svg
  const renderEventDots = () => {
    if (events.length === 0) {
      return null;
    }

    return (
      <g className="narrative-chart__events">
        {events.map((event) => {
          // if the end is null, it's an "ongoing" event
          const end = event.end ? parseISO(event.end) : R.last(xScale.domain());

          return (
            <g key={"" + event.start + event.end + event.href}>
              {/* on hover, show the range line on the axis */}
              {hoveredYear === event.start && event.end !== event.start && (
                <g>
                  <line
                    x1={xScale(parseISO(event.start))}
                    y1={height}
                    x2={xScale(end)}
                    y2={height}
                    stroke="white"
                  />
                  <circle
                    stroke="white"
                    strokeWidth="1px"
                    fill="white"
                    r={2}
                    cx={xScale(end)}
                    cy={height}
                  />
                </g>
              )}

              {/* event circle (on top)*/}
              <circle
                stroke="white"
                strokeWidth="1px"
                fill={hoveredYear === event.start ? "#ff7700" : "currentColor"}
                r={6}
                cx={xScale(parseISO(event.start))}
                cy={height}
              />
            </g>
          );
        })}
      </g>
    );
  };

  // render the event details in html, so we get wrapping for free
  const renderEventsDiv = () => {
    if (!events || events.length === 0) {
      return null;
    }

    const eventGroups = R.compose(
      R.map((pair) => ({
        year: pair[0],
        events: pair[1],
      })),
      R.toPairs,
      R.groupBy(R.prop("start")),
    )(events);

    return (
      <div
        className="narrative-chart__events-container"
        style={{
          position: "relative",
          marginLeft: margin.left,
          height: eventsAreaHeight,
          // backgroundColor: "rgba(255, 255, 255, 0.2)",
        }}
      >
        {eventGroups.map((group, i) => {
          const dottedLineSvgHeight = 46;

          const groupLeft = xScale(parseISO(group.year)) - 6; // 6 is half the circle size;

          // if it's near the right edge, nudge it left
          const targetEventsWidth = Math.min(width, 200 * group.events.length);
          const nudge = Math.min(0, width - groupLeft - targetEventsWidth);
          const eventsWidth = nudge ? targetEventsWidth : undefined;

          return (
            <div
              key={group.year}
              className="narrative-chart__event-group"
              style={{
                position: "absolute",
                visibility: group.year === hoveredYear ? "visible" : "hidden",
                left: groupLeft,
                right: 0,
              }}
            >
              {/* dotted line  */}
              <svg height={dottedLineSvgHeight} width={12}>
                <line
                  x1={6}
                  y1={0}
                  x2={6}
                  y2={dottedLineSvgHeight}
                  stroke="white"
                  strokeWidth="1.5px"
                  strokeDasharray="2, 8"
                />
              </svg>
              <div
                className="narrative-chart__event-group-events"
                style={{
                  position: "relative",
                  marginLeft: nudge,
                  width: eventsWidth,
                }}
              >
                {group.events.map((event) => {
                  return (
                    <div
                      key={event.start + event.end}
                      className="narrative-chart__event"
                    >
                      <div className="narrative-chart__event-title">
                        {event.start}{" "}
                        {event.end !== event.start &&
                          `- ${event.end || "Ongoing"}`}
                      </div>
                      <div
                        className="narrative-chart__event-text"
                        dangerouslySetInnerHTML={{ __html: event.text }}
                      />
                      {event.href && (
                        <a
                          className="narrative-chart__event-link"
                          href={event.href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {event.label}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="narrative-chart">
      <div
        className="narrative-chart__popup-container"
        style={{
          position: "absolute",
          top: margin.top,
          left: margin.left,
          width: width,
          height: height,
          pointerEvents: "none",
        }}
      >
        <Popup
          isHidden={!popupInfo}
          offset={12}
          triangleSide="left"
          flipToContain=".narrative-chart"
          nudgeToContain=".narrative-chart"
          {...popupInfo}
        />
      </div>
      <svg width={svgWidth} height={svgHeight} ref={svgRef}>
        <XAxisTrend
          className="narrative-chart__x-axis"
          height={height}
          margin={margin}
          scale={xScale}
        />
        <YAxis
          gridLines
          className="narrative-chart__y1-axis"
          margin={margin}
          width={width}
          height={height}
          scale={y1Scale}
          dataType={primaryData.dataType}
        />
        {y2Scale && (
          <YAxisRight
            className="narrative-chart__y2-axis"
            margin={margin}
            width={width}
            height={height}
            dataType={secondaryDataType || otherLinesDataType}
            scale={y2Scale}
          />
        )}
        <g
          className="narrative-chart__chart-area"
          transform={`translate(${margin.left}, ${margin.top})`}
        >
          <rect
            className="narrative-chart__chart-hit-area"
            x="0"
            y={-margin.top}
            width={width}
            height={height + margin.top + margin.bottom}
            style={{ fill: "red", opacity: 0 }}
          />

          {renderOtherLines()}

          {secondaryData && (
            <TrendLine
              className="narrative-chart__secondary"
              color="currentColor"
              dataPoints={secondaryData.dataPoints}
              height={height}
              xScale={xScale}
              yScale={y2Scale || y1Scale} // the secondaryData might share the y1Scale
              dottedLineForMissingData={dottedLineForMissingData}
              onExcludedHover={setPopupInfo}
              label={showLineLabels ? secondaryData.name : ""}
              excludeUrl={excludeUrl}
            />
          )}

          <TrendLine
            className="narrative-chart__primary"
            color="white"
            dataPoints={primaryData.dataPoints}
            height={height}
            xScale={xScale}
            yScale={y1Scale}
            dottedLineForMissingData={dottedLineForMissingData}
            onExcludedHover={setPopupInfo}
            label={showLineLabels ? primaryData.name : ""}
            excludeUrl={excludeUrl}
          />

          <YearHoverLine
            {...{ onYearHover, hoveredYear, xScale, svgRef, height, margin }}
          />

          {showValueOnHover && hoveredDataPoint && (
            <g
              className="narrative-chart__year-hover-value"
              transform={
                "translate(" +
                (xScale(parseISO(hoveredYear)) - 4) +
                ", " +
                y1Scale(hoveredDataPoint.value) +
                ")"
              }
              textAnchor="end"
            >
              <text fill="black" stroke="black" strokeWidth="2px">
                {hoveredDataPoint.displayValue}
              </text>
              <text fill="white">{hoveredDataPoint.displayValue}</text>
            </g>
          )}
          {renderEventDots()}
        </g>
      </svg>
      {renderEventsDiv()}
    </div>
  );
};

NarrativeChart.propTypes = propTypes;
NarrativeChart.defaultProps = defaultProps;
export default NarrativeChart;
